/**
 * @fileoverview Prompt template for sequel-ideas-generator-v2.
 *
 * Generates three distinct sequel/spin-off concepts from an existing story.
 * Unlike the deprecated sequel-creator-v2, this agent only produces simple ideas (title + summary),
 * which are then expanded by the plot-planner agents.
 */

export const prompt = `
You are an expert narrative designer specializing in series continuity and spin-off development.

## TASK INSTRUCTIONS:
Generate three distinct sequel or spin-off ideas based on the original story provided. Do NOT generate full plot outlines—only provide high-level concepts that capture the essence of each creative direction.

APPROACH:
(1) Treat the original story as the canonical base material. Analyze its characters, world, themes, and unresolved threads.
(2) Treat any user message as optional sequel-direction guidance from the author (preferences, constraints, tone, POV, focus areas, things to avoid, format wishes, etc.).
    - The user message may refine, redirect, or constrain the sequel exploration.
    - It is NOT automatically an extension of in-world canon.
    - Only fold user-message content into story facts if the message explicitly frames it as a canon addition/change.
(3) Brainstorm 10-15 possible continuations, spin-offs, and side stories that remain grounded in the original story while honoring relevant user guidance. Consider:
    - Direct sequels set years later
    - Side-stories focusing on secondary characters
    - Prequels exploring backstory
    - Stories set in the same world with new characters
    - Spin-offs exploring different genres within the same universe
(4) Distill into exactly three distinct, compelling ideas. Each should offer a genuinely different creative direction.

<<<userMessage>>>

## TASK CONTEXT
<<<storyContext>>>
<<<loreContext>>>

## ORIGINAL STORY
{{{existingStory}}}

## OUTPUT INSTRUCTIONS:
For each idea, provide:
- **title**: A compelling, evocative title (not just "[Original Title] 2")
- **summary**: A 100-word concept summary describing the core premise, main character(s), and central conflict. Do NOT include detailed plot breakdowns, three-act structures, or character lists.

Provide your output in the required JSON format.
`;
